import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { getPosition, reverseGeocode, fmtCoords, accuracyLabel } from '../../utils/geo';

const fmtErr = (d, fallback) => {
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg ? `${(e.loc || []).slice(-1)[0] || 'field'}: ${e.msg}` : JSON.stringify(e)).join('; ');
  if (typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return String(d);
};

const C = {
  navy: '#2b1b22', muted: '#8b7681', border: '#ece3e7', primary: '#7b1e3c',
};

function Field({ id, label, type = 'text', value, onChange, placeholder, autoComplete, required = true, hint }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={id} style={{
        display: 'block', fontSize: 11, fontWeight: 700, color: '#1e3348',
        marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px',
      }}>
        {label}{!required && <span style={{ color: C.muted, fontWeight: 500, textTransform: 'none' }}> (optional)</span>}
      </label>
      <input
        id={id} type={type} value={value} onChange={onChange}
        placeholder={placeholder} autoComplete={autoComplete} required={required}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box', padding: '11px 13px',
          borderRadius: 10, fontSize: 14, color: C.navy, fontFamily: 'Inter, sans-serif',
          outline: 'none', background: '#fff',
          border: `1px solid ${focused ? C.primary : C.border}`,
          boxShadow: focused ? '0 0 0 4px rgba(123,30,60,0.12)' : '0 1px 2px rgba(13,27,46,0.04)',
          transition: 'border-color .15s, box-shadow .15s',
        }}
      />
      {hint && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 5, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

export default function Signup() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirm: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'otp' | 'done'
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Email-OTP step — required to activate the account (no admin approval).
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  // Optional GPS capture — only on explicit click (browser permission prompt)
  const [geo, setGeo] = useState(null);          // {lat, lng, address}
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoErr, setGeoErr] = useState('');

  const captureLocation = async () => {
    setGeoBusy(true);
    setGeoErr('');
    try {
      const { lat, lng, accuracy } = await getPosition();
      const address = await reverseGeocode(lat, lng);
      setGeo({ lat, lng, accuracy, address: address || fmtCoords(lat, lng) });
    } catch (e) {
      setGeoErr(e.message);
    } finally {
      setGeoBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      await authAPI.signup({
        name: form.name, password: form.password,
        email: form.email, phone: form.phone || undefined,
        latitude: geo?.lat, longitude: geo?.lng,
        address: geo?.address || undefined,
      });
      setStep('otp');
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail, 'Could not submit the request. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    setOtpError('');
    try {
      await authAPI.verifySignupOtp(form.email, otp.trim());
      setStep('done');
    } catch (err) {
      setOtpError(fmtErr(err.response?.data?.detail, 'Incorrect or expired code. Please try again.'));
    } finally {
      setOtpLoading(false);
    }
  };

  const resendOtp = async () => {
    setResendBusy(true);
    setResendMsg('');
    setOtpError('');
    try {
      await authAPI.resendSignupOtp(form.email);
      setResendMsg('A new code has been sent.');
    } catch (err) {
      setOtpError(fmtErr(err.response?.data?.detail, 'Could not resend the code — please wait a moment and try again.'));
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f4f6fa', fontFamily: 'Inter, sans-serif', padding: '40px 16px',
    }}>
      <div style={{
        width: '100%', maxWidth: 460, background: '#fff', borderRadius: 16,
        padding: '38px 36px', border: '1px solid #ece3e7',
        boxShadow: '0 1px 2px rgba(13,27,46,0.04), 0 12px 32px -8px rgba(13,27,46,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: 'linear-gradient(135deg,#7b1e3c,#c39a4e)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 19, fontWeight: 900, color: '#fff',
          }}>H</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.navy }}>HHMS</div>
        </div>

        {step === 'done' ? (
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: C.navy, margin: '0 0 10px' }}>
              Account activated ✓
            </h1>
            <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.65, margin: '0 0 20px' }}>
              Thanks, <strong style={{ color: C.navy }}>{form.name}</strong>. Your account is
              active — sign in with <code style={{
                background: '#f7f3f4', border: `1px solid ${C.border}`, borderRadius: 4,
                padding: '1px 6px', fontSize: 12 }}>{form.email}</code> to request care.
            </p>
            <Link to="/login" style={{
              display: 'inline-block', padding: '11px 18px', borderRadius: 10,
              background: 'linear-gradient(180deg,#94335a,#7b1e3c)', color: '#fff',
              fontSize: 13.5, fontWeight: 700, textDecoration: 'none',
            }}>Sign in</Link>
          </div>
        ) : step === 'otp' ? (
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: C.navy, margin: '0 0 6px' }}>
              Verify your email
            </h1>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: '0 0 20px' }}>
              We sent a 6-digit code to <strong style={{ color: C.navy }}>{form.email}</strong>.
              Enter it below to activate your account — no waiting for approval.
            </p>

            {otpError && (
              <div role="alert" style={{
                background: '#fdecea', color: '#b71c1c', padding: '10px 13px',
                borderRadius: 10, marginBottom: 16, fontSize: 12.5,
                border: '1px solid #f5c2c0', lineHeight: 1.5,
              }}>{otpError}</div>
            )}
            {resendMsg && !otpError && (
              <div style={{
                background: 'rgba(43,182,115,0.08)', color: '#1d7a4d', padding: '10px 13px',
                borderRadius: 10, marginBottom: 16, fontSize: 12.5,
                border: '1px solid rgba(43,182,115,0.3)', lineHeight: 1.5,
              }}>{resendMsg}</div>
            )}

            <form onSubmit={verifyOtp} noValidate>
              <Field id="otp" label="6-digit code" value={otp}
                onChange={(e) => setOtp(e.target.value)} placeholder="123456"
                autoComplete="one-time-code" hint="Check your inbox (and spam folder)." />
              <button type="submit" disabled={otpLoading} style={{
                width: '100%', padding: '12px 16px', marginTop: 4,
                background: otpLoading ? '#90a4ae' : 'linear-gradient(180deg,#94335a,#7b1e3c)',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700, cursor: otpLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}>
                {otpLoading ? 'Verifying…' : 'Verify & activate'}
              </button>
            </form>

            <p style={{ textAlign: 'center', fontSize: 12.5, color: C.muted, marginTop: 18, marginBottom: 0 }}>
              Didn't get a code?{' '}
              <button type="button" onClick={resendOtp} disabled={resendBusy} style={{
                background: 'none', border: 'none', padding: 0, font: 'inherit',
                color: C.primary, fontWeight: 700, cursor: resendBusy ? 'wait' : 'pointer',
              }}>
                {resendBusy ? 'Sending…' : 'Resend code'}
              </button>
            </p>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: C.navy, margin: '0 0 6px' }}>
              Create a patient account
            </h1>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: '0 0 20px' }}>
              For patients and their families. Verify your email with a quick code and
              you're in. Staff accounts are issued by invitation only.
            </p>

            {error && (
              <div role="alert" style={{
                background: '#fdecea', color: '#b71c1c', padding: '10px 13px',
                borderRadius: 10, marginBottom: 16, fontSize: 12.5,
                border: '1px solid #f5c2c0', lineHeight: 1.5,
              }}>{error}</div>
            )}

            <form onSubmit={submit} noValidate>
              <Field id="name" label="Full name" value={form.name} onChange={set('name')}
                placeholder="e.g. Jane Wanjiku" autoComplete="name" />
              <Field id="email" label="Email" type="email" value={form.email} onChange={set('email')}
                placeholder="you@example.com" autoComplete="email"
                hint="This is what you'll sign in with." />
              <Field id="phone" label="Phone" value={form.phone} onChange={set('phone')}
                placeholder="+254 7XX XXX XXX" autoComplete="tel" required={false} />
              <Field id="password" label="Password" type="password" value={form.password}
                onChange={set('password')} placeholder="Choose a strong password" autoComplete="new-password"
                hint="At least 10 characters with upper & lower case, a digit and a symbol." />
              <Field id="confirm" label="Confirm password" type="password" value={form.confirm}
                onChange={set('confirm')} placeholder="Repeat the password" autoComplete="new-password" />

              {/* Optional home location — helps us route the nearest nurse */}
              <div style={{
                marginBottom: 16, padding: '12px 14px', borderRadius: 10,
                background: '#f7f3f4', border: `1px dashed ${C.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                    <span style={{ display: 'block', fontWeight: 700, color: '#1e3348', marginBottom: 2 }}>
                      📍 Home location <span style={{ fontWeight: 500, color: C.muted }}>(optional)</span>
                    </span>
                    Lets us send the nearest nurse to you — captured only when you tap the button.
                  </div>
                  <button type="button" onClick={captureLocation} disabled={geoBusy} style={{
                    flexShrink: 0, background: '#fff', border: `1px solid ${C.primary}`,
                    borderRadius: 8, padding: '7px 12px', fontSize: 11.5, fontWeight: 700,
                    color: C.primary, cursor: geoBusy ? 'wait' : 'pointer', fontFamily: 'Inter, sans-serif',
                  }}>
                    {geoBusy ? 'Locating…' : geo ? 'Update' : 'Use my location'}
                  </button>
                </div>
                {geo && (
                  <div style={{
                    marginTop: 9, fontSize: 11.5, color: '#1d7a4d', lineHeight: 1.5,
                    background: 'rgba(43,182,115,0.08)', border: '1px solid rgba(43,182,115,0.3)',
                    borderRadius: 8, padding: '7px 10px',
                  }}>
                    ✓ {geo.address}
                    {accuracyLabel(geo.accuracy) && (
                      <span style={{ display: 'block', color: '#9a6b00', marginTop: 2 }}>
                        ⚠ {accuracyLabel(geo.accuracy)}
                      </span>
                    )}
                  </div>
                )}
                {geoErr && (
                  <div style={{ marginTop: 9, fontSize: 11.5, color: '#b71c1c', lineHeight: 1.5 }}>
                    {geoErr}
                  </div>
                )}
              </div>

              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '12px 16px', marginTop: 4,
                background: loading ? '#90a4ae' : 'linear-gradient(180deg,#94335a,#7b1e3c)',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}>
                {loading ? 'Submitting…' : 'Request my account'}
              </button>
            </form>

            <p style={{ textAlign: 'center', fontSize: 12.5, color: C.muted, marginTop: 18, marginBottom: 0 }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: C.primary, fontWeight: 700, textDecoration: 'none' }}>
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
